import LazyYouTubeEmbed from '@/commons/LazyYouTubeEmbed';
import React from 'react';
import { FaFan, FaSnowflake, FaWater, FaPumpSoap, FaRegCheckCircle, FaThermometerHalf } from 'react-icons/fa';
import { localiseTerms } from '../../../../utils/markets';

const steps = [
    {
        icon: <FaFan className="text-xl md:text-3xl text-orange-600" />,
        title: 'Air Filter & Front Panel/Cover Cleaning',
        description:
        'Dirty air filters can block airflow and strain the aircon. Technicians clean or jet-wash filters to restore optimal airflow.',
    },
    {
        icon: <FaSnowflake className="text-xl md:text-3xl text-orange-600" />,
        title: 'Cleaning of Evaporator Coil',
        description:
        'The evaporator coil can collect dirt, reducing efficiency. The technician cleans it using a soft brush or vacuum.',
    },
    {
        icon: <FaWater className="text-xl md:text-3xl text-orange-600" />,
        title: 'Drain Pan or Tray Cleaning',
        description:
        'Clogged drain pans may lead to leaks. Technicians clean out debris from the pan to ensure proper drainage.',
    },
    {
        icon: <FaPumpSoap className="text-xl md:text-3xl text-orange-600" />,
        title: 'Vacuuming of Drainage Pipe',
        description:
        'To prevent water backup, the drainage pipe is vacuumed to remove blockages that may disrupt water flow.',
    },
    {
        icon: <FaRegCheckCircle className="text-xl md:text-3xl text-blue-500" />,
        title: 'Outdoor Condenser Check',
        description:
            'Technicians assess the outdoor unit’s performance and recommend cleaning if necessary, based on inspection or customer request (extra charges may apply).',
    },
    {
        icon: <FaThermometerHalf className="text-xl md:text-3xl text-blue-500" />,
        title: 'Monitor Refrigerant Gas Level',
        description:
            'Technicians check the refrigerant gas level if poor cooling is observed or upon client request, and may recommend top-up if needed.',
    },
];

// Optional props (all absent on the Singapore pages, which render exactly as
// before): `market` localises the terms and hides the Singapore-filmed video;
// `steps`, `title` and `intro` replace the copy, so the UAE / Malaysia pages
// can show their own wording (page sections must not repeat the SG text).
export const AirconGeneralServiceProcess = ({ market, steps: customSteps, title, intro } = {}) => {
  const items = (customSteps ?? steps).map((step, i) => ({
    ...step,
    icon: step.icon ?? steps[i % steps.length].icon,
    title: localiseTerms(step.title, market),
    description: localiseTerms(step.description, market),
  }));

  return (
    <section aria-labelledby="aircon-process-title" className="px-2">
      <h2 id="aircon-process-title" className="text-xl md:text-2xl text-center font-bold uppercase text-gray-800">
        {title ?? (market ? localiseTerms(`${items.length}-Step Aircon Servicing Process`, market) : '6-Step Aircon Servicing Process')}
      </h2>

      {intro ? (
        <p className="text-center leading-6 font-medium mt-3 md:mt-6 text-gray-600">{intro}</p>
      ) : !market && (
        <p className="text-center leading-6 font-medium mt-3 md:mt-6 text-gray-600">
          At StringsSG, we follow a proven <strong>6-step approach</strong> to general aircon servicing in Singapore — ensuring your unit runs efficiently and reliably.
        </p>
      )}

      {/* Mobile fold budget: cards were centre-stacked (icon above title above copy) at
          p-6, so six steps ran ~1,700px on a phone. The icon now sits inline with the
          heading and padding/gaps are tightened on mobile. Desktop layout unchanged. */}
      <ul className="mt-4 md:mt-10 grid gap-3 md:gap-6 md:grid-cols-2 lg:grid-cols-3" aria-label={localiseTerms("Aircon general servicing steps", market)}>
        {items.map((step, index) => (
          <li
            key={index}
            className="bg-white rounded-2xl shadow-md hover:shadow-xl transition p-3 md:p-6 flex flex-col md:items-center md:text-center"
            aria-labelledby={`step-title-${index}`}
          >
            <h3 id={`step-title-${index}`} className="md:text-lg font-semibold text-gray-800 flex items-center gap-2 md:flex-col md:gap-0">
              <span className="shrink-0 text-xl md:text-3xl md:mt-0">{step.icon}</span>
              <span className="md:mt-4">{index + 1}. {step.title}</span>
            </h3>
            <p className="text-sm leading-6 text-gray-600 mt-1 md:mt-2">{step.description}</p>
          </li>
        ))}
      </ul>
      
      {!market && (
        <div className="mt-6 md:mt-12">
          <h3 className="text-center text-lg font-semibold text-gray-500 mb-3 md:mb-4">
            Watch the Step-by-Step Servicing Video
          </h3>
          <LazyYouTubeEmbed videoId="Pa5-iT9WkV8" title="Steps involved in Aircon Servicing in Singapore" />
        </div>
      )}
    </section>
  );
};
